import type { CanvasRenderingTarget2D } from 'fancy-canvas';
import type {
  IChartApi,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesApi,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  SeriesType,
  Time,
} from 'lightweight-charts';

export interface ChartSegment { t1: Time; v1: number; t2: Time; v2: number; }

export interface SegmentLinesStyle { color: string; lineWidth: number; dashed: boolean; }

/**
 * Dibuja N segmentos independientes (p.ej. entrada→salida de cada operación) en un solo trazo
 * sobre la serie a la que se engancha. Sustituye a una LineSeries por segmento: con miles de
 * operaciones, cada serie extra encarece la fusión del eje de tiempo, el autoscale y cada frame.
 * Los tiempos deben existir en el eje (ajustados a una vela), si no el segmento no se pinta.
 */
export class SegmentLinesPrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private series: ISeriesApi<SeriesType, Time> | null = null;
  private requestUpdate: (() => void) | null = null;
  private segments: ChartSegment[] = [];
  private readonly view: IPrimitivePaneView;

  constructor(private readonly style: SegmentLinesStyle) {
    const renderer: IPrimitivePaneRenderer = {
      draw: (target: CanvasRenderingTarget2D) => this.draw(target),
    };
    this.view = { renderer: () => renderer };
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this.chart = param.chart;
    this.series = param.series;
    this.requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  /** Serie a la que está enganchado (null si no lo está). */
  get attachedSeries(): ISeriesApi<SeriesType, Time> | null {
    return this.series;
  }

  paneViews(): readonly IPrimitivePaneView[] {
    return [this.view];
  }

  setSegments(segments: ChartSegment[]): void {
    this.segments = segments;
    this.requestUpdate?.();
  }

  setColor(color: string): void {
    this.style.color = color;
    this.requestUpdate?.();
  }

  private draw(target: CanvasRenderingTarget2D): void {
    if (!this.chart || !this.series || !this.segments.length) return;
    const timeScale = this.chart.timeScale();
    const series = this.series;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const width = mediaSize.width;
      ctx.save();
      ctx.strokeStyle = this.style.color;
      ctx.lineWidth = this.style.lineWidth;
      ctx.setLineDash(this.style.dashed ? [4, 4] : []);
      ctx.beginPath();
      for (const s of this.segments) {
        const x1 = timeScale.timeToCoordinate(s.t1);
        const x2 = timeScale.timeToCoordinate(s.t2);
        if (x1 === null || x2 === null || x2 < 0 || x1 > width) continue;
        const y1 = series.priceToCoordinate(s.v1);
        const y2 = series.priceToCoordinate(s.v2);
        if (y1 === null || y2 === null) continue;
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
      }
      ctx.stroke();
      ctx.restore();
    });
  }
}
